/* eslint-disable react-hooks/set-state-in-effect, react-hooks/purity, react-hooks/set-state-in-render --
 * v77.99.77: Effect-driven data fetch with mounted-guard; intentional.
 */
// v77.99.41 — PaymentHistorySheet
//
// Renders the per-occurrence audit trail for a single maintenance
// payment. Powered by GET /api/maintenance/payments/{id}/history.
// Shows each action as "<who> · <when> · prev → new" with optional
// shared/private indicator and amount-paid context.

import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ArrowRight, Clock, Eye, Lock, RotateCcw, CheckCircle2 } from "lucide-react";

const STATUS_LABEL = {
  awaiting: "Awaiting payment",
  due: "Awaiting payment",
  paid: "Paid on time",
  paid_late: "Paid late",
  part_paid: "Part-paid",
  overpaid: "Overpaid",
  late: "Overdue",
  missed: "Missed",
  waived: "Waived",
  disputed: "Disputed",
};

function fmtTs(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
  } catch { return iso; }
}

export default function PaymentHistorySheet({ open, paymentId, onClose }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !paymentId) return;
    let cancelled = false;
    setLoading(true);
    api.get(`/maintenance/payments/${encodeURIComponent(paymentId)}/history`)
      .then((r) => { if (!cancelled) setRows(r.data?.history || []); })
      .catch(() => { if (!cancelled) setRows([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, paymentId]);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="maintenance-history-sheet">
        <DialogHeader>
          <DialogTitle className="font-heading text-base inline-flex items-center gap-2">
            <Clock className="w-4 h-4 text-[#116B6E]" strokeWidth={1.7} /> Payment history
          </DialogTitle>
        </DialogHeader>
        {loading ? (
          <div className="text-sm text-[#6F828A] py-6 text-center">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="text-sm text-[#6F828A] py-6 text-center" data-testid="maintenance-history-empty">
            Nothing recorded yet for this payment.
          </div>
        ) : (
          <ol className="space-y-2 max-h-[480px] overflow-y-auto pr-1">
            {rows.slice().reverse().map((h, idx) => {
              const ActionIcon = h.action === "reset" ? RotateCcw : CheckCircle2;
              return (
                <li
                  key={`${h.at}-${idx}`}
                  className="flex items-start gap-2 p-2.5 border border-[#EEF3F4] bg-[#F4FAFA] rounded-sm"
                  data-testid="maintenance-history-row"
                >
                  <ActionIcon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${h.action === "reset" ? "text-[#6F4E1E]" : "text-[#0F5658]"}`} strokeWidth={1.8} />
                  <div className="flex-1 min-w-0 text-xs">
                    <div className="text-[#2E3A3F] inline-flex items-center gap-1 flex-wrap">
                      <span className="font-medium">{STATUS_LABEL[h.previous_status] || h.previous_status || "—"}</span>
                      <ArrowRight className="w-3 h-3 text-[#6F828A]" strokeWidth={2} />
                      <span className="font-medium text-[#116B6E]">{STATUS_LABEL[h.status] || h.status || "—"}</span>
                      {typeof h.is_shared === "boolean" && (
                        <span className={`inline-flex items-center gap-0.5 text-[10px] uppercase tracking-[0.08em] px-1.5 py-0.5 border rounded-sm ml-1 ${h.is_shared ? "border-[#4A6170] bg-[#E6ECEF] text-[#2E3E48]" : "border-[#D9E1E3] bg-[#EEF3F4] text-[#6F828A]"}`}>
                          {h.is_shared ? <Eye className="w-2.5 h-2.5" /> : <Lock className="w-2.5 h-2.5" />}
                          {h.is_shared ? "Shared" : "Private"}
                        </span>
                      )}
                    </div>
                    <div className="text-[#6F828A] mt-0.5">
                      {h.by_name} · {fmtTs(h.at)}
                      {h.amount_paid != null && ` · €${Number(h.amount_paid).toFixed(2)} paid`}
                    </div>
                    {h.note && (
                      <div className="text-[#3A4548] mt-1 italic">“{h.note}”</div>
                    )}
                  </div>
                </li>
              );
            })}
          </ol>
        )}
      </DialogContent>
    </Dialog>
  );
}
